import { motion } from 'framer-motion';
import { RefreshCw, Play, Pause, Trash2, Pencil } from 'lucide-react';
import { useRecurringTemplates, usePauseTemplate, useResumeTemplate, useDeleteTemplate } from '../../hooks/useRecurrence';
import { useAppStore } from '../../store';
import type { Task } from '../../types';

export function RecurringView() {
  const templates = useRecurringTemplates();
  const pauseMutation = usePauseTemplate();
  const resumeMutation = useResumeTemplate();
  const deleteMutation = useDeleteTemplate();

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center">
          <RefreshCw size={18} className="text-accent" />
        </div>
        <div>
          <h2 className="text-body font-bold text-text-primary">Recurring Tasks</h2>
          <p className="text-caption text-text-muted">Tasks that repeat on a schedule</p>
        </div>
      </div>

      {templates.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="surface-card p-8 text-center"
        >
          <div className="w-12 h-12 rounded-xl bg-accent/8 flex items-center justify-center mx-auto mb-3">
            <RefreshCw size={22} className="text-accent/50" />
          </div>
          <p className="text-body font-medium text-text-muted mb-1">No recurring tasks</p>
          <p className="text-caption text-text-muted/70">Open any task and toggle "Repeat" to create one</p>
        </motion.div>
      ) : (
        <div className="space-y-2">
          {templates.map((template, i) => (
            <TemplateCard
              key={template.id}
              template={template}
              index={i}
              onPause={() => pauseMutation.mutate(template.id)}
              onResume={() => resumeMutation.mutate(template.id)}
              onDelete={() => {
                if (confirm(`Delete "${template.title}" and all its future occurrences?`)) {
                  deleteMutation.mutate(template.id);
                }
              }}
              onEdit={() => useAppStore.getState().setSelectedTaskId(template.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({ template, index, onPause, onResume, onDelete, onEdit }: {
  template: Task;
  index: number;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => void;
  onEdit: () => void;
}) {
  const isPaused = !!template.recurrencePaused;
  const ruleText = `Every ${template.recurrenceInterval} ${template.recurrenceUnit}`;
  const timeText = template.defaultScheduledTime ? ` at ${template.defaultScheduledTime}` : '';

  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: index * 0.04, ease: [0.25, 1, 0.5, 1] }}
      className={`surface-card p-4 flex items-center gap-4 group ${isPaused ? 'opacity-60' : ''}`}
    >
      <div className={`w-2.5 h-2.5 rounded-full ${isPaused ? 'bg-p2' : 'bg-finish'}`} />

      <div className="flex-1 min-w-0">
        <p className="text-body font-semibold text-text-primary truncate">{template.title}</p>
        <p className="text-caption text-text-muted">
          {ruleText}{timeText}
          {isPaused && <span className="text-p2 ml-2">Paused</span>}
          {template.recurrenceEndDate && <span className="ml-2">until {template.recurrenceEndDate}</span>}
        </p>
      </div>

      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {isPaused ? (
          <button onClick={onResume} className="p-1.5 rounded-lg text-finish hover:bg-finish/10 transition-colors" title="Resume">
            <Play size={14} />
          </button>
        ) : (
          <button onClick={onPause} className="p-1.5 rounded-lg text-p2 hover:bg-p2/10 transition-colors" title="Pause">
            <Pause size={14} />
          </button>
        )}
        <button onClick={onEdit} className="p-1.5 rounded-lg text-text-muted hover:text-accent hover:bg-accent/10 transition-colors" title="Edit">
          <Pencil size={14} />
        </button>
        <button onClick={onDelete} className="p-1.5 rounded-lg text-text-muted hover:text-p1 hover:bg-p1/10 transition-colors" title="Delete">
          <Trash2 size={14} />
        </button>
      </div>
    </motion.div>
  );
}
